import React, { useState, useRef, useEffect } from "react";
import { X, Send, Phone, Paperclip, MoreVertical } from "lucide-react";
import { useLocation } from "react-router-dom";
import { sendChatMessage, getTeamPublic } from "../lib/api";

function getAgentStatus() {
    const now = new Date();
    const h = now.getHours();
    const day = now.getDay();
    if (day === 0 || day === 6) return { status: "offline", label: "Víkend - odpovíme v Po", color: "bg-gray-400" };
    if (h >= 9 && h < 12) return { status: "online", label: "Online", color: "bg-green-500" };
    if (h >= 12 && h < 13) return { status: "lunch", label: "Na obědě - vrátím se ve 13:00", color: "bg-yellow-500" };
    if (h >= 13 && h < 17) return { status: "online", label: "Online", color: "bg-green-500" };
    return { status: "offline", label: "Mimo kancelář - odpovím zítra", color: "bg-gray-400" };
}

function formatTime(d) {
    return new Date(d).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
}

export default function ChatbotWidget() {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [agent, setAgent] = useState(null);
    const [typing, setTyping] = useState(false);
    const [showTeam, setShowTeam] = useState(false);
    const [team, setTeam] = useState([]);
    const scrollRef = useRef(null);
    const agentStatus = getAgentStatus();

    useEffect(() => {
        getTeamPublic().then(r => {
            const members = r.data || [];
            setTeam(members);
            const obch = members.find(m => m.role === "obchodnik") || members[0];
            if (obch) setAgent(obch);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, typing]);

    useEffect(() => {
        if (open && messages.length === 0 && agent) {
            const greeting = agentStatus.status === "lunch"
                ? `Dobrý den! Momentálně jsem na obědě. Napište mi zprávu a ozvu se po 13:00. Nebo zavolejte na ${agent.phone}.`
                : agentStatus.status === "offline"
                ? `Dobrý den! Jsem teď mimo kancelář. Napište mi zprávu a odpovím co nejdříve, nebo zavolejte na ${agent.phone}.`
                : `Dobrý den! Jsem ${agent.name?.split(" ")[0]}, ${agent.title || "obchodník Solar-Green"}. Jak vám mohu pomoci?`;
            setMessages([{ role: "agent", text: greeting, time: new Date().toISOString() }]);
        }
    }, [open, agent]);

    const handleSend = async () => {
        if (!input.trim() || loading) return;
        const userMsg = input.trim();
        setInput("");
        setMessages(prev => [...prev, { role: "user", text: userMsg, time: new Date().toISOString() }]);
        setLoading(true);
        setTyping(true);

        // Natural delay before "reading" and "typing"
        const delay = 800 + Math.random() * 1200;
        await new Promise(r => setTimeout(r, delay));

        try {
            const res = await sendChatMessage({ message: userMsg, session_id: sessionId });
            const data = res.data;
            setSessionId(data.session_id);
            setTyping(false);
            setMessages(prev => [...prev, { role: "agent", text: data.reply, time: new Date().toISOString() }]);
        } catch {
            setTyping(false);
            setMessages(prev => [...prev, { role: "agent", text: `Omlouvám se, nastala chyba. Zavolejte mi prosím na ${agent?.phone || "telefon"}.`, time: new Date().toISOString() }]);
        } finally {
            setLoading(false);
        }
    };

    const initials = agent?.name?.split(" ").map(w => w[0]).join("").slice(0, 2) || "SG";

    // Hide chatbot on admin pages
    if (location.pathname.startsWith("/sprava")) return null;

    return (
        <>
            {/* Floating Button */}
            {!open && (
                <button onClick={() => setOpen(true)}
                    className="fixed bottom-5 right-5 z-[300] flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-full bg-white shadow-xl border border-gray-200 hover:shadow-2xl hover:scale-[1.02] transition-all duration-200 group"
                    data-testid="chatbot-open-btn">
                    <div className="relative">
                        {agent?.photo_url ? (
                            <img src={agent.photo_url} alt={agent.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-sm">{initials}</div>
                        )}
                        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${agentStatus.color}`}></span>
                    </div>
                    <div className="text-left hidden sm:block">
                        <p className="text-xs font-semibold text-gray-900 leading-tight">{agent?.name || "Solar-Green"}</p>
                        <p className="text-[10px] text-gray-500 leading-tight">{agentStatus.label}</p>
                    </div>
                </button>
            )}

            {/* Chat Window */}
            {open && (
                <div className="fixed bottom-5 right-5 z-[300] w-[380px] max-w-[calc(100vw-40px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
                    style={{ height: "min(560px, calc(100vh - 100px))" }} data-testid="chatbot-window">

                    {/* Header - looks like real person */}
                    <div className="bg-white border-b px-4 py-3 flex items-center gap-3 flex-shrink-0">
                        <div className="relative flex-shrink-0">
                            {agent?.photo_url ? (
                                <img src={agent.photo_url} alt={agent.name} className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-sm">{initials}</div>
                            )}
                            <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${agentStatus.color}`}></span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 leading-tight">{agent?.name || "Solar-Green"}</p>
                            <p className="text-[11px] text-gray-500 leading-tight">{agent?.title || "Obchodní oddělení"}</p>
                            <p className={`text-[10px] leading-tight font-medium ${agentStatus.status === "online" ? "text-green-600" : agentStatus.status === "lunch" ? "text-yellow-600" : "text-gray-400"}`}>
                                {agentStatus.label}
                            </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            {agent?.phone && (
                                <a href={`tel:${agent.phone}`} className="p-2 rounded-full hover:bg-green-50 text-green-600 transition-colors" title="Zavolat" data-testid="chatbot-call-btn">
                                    <Phone size={16} />
                                </a>
                            )}
                            <button onClick={() => setShowTeam(t => !t)} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 transition-colors" title="Náš tým" data-testid="chatbot-team-toggle">
                                <MoreVertical size={16} />
                            </button>
                            <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 transition-colors" data-testid="chatbot-close-btn">
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    {showTeam ? (
                        /* Team panel */
                        <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50" data-testid="chatbot-team-view">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase px-1">Náš tým - kontaktujte nás</p>
                            {team.map(m => (
                                <div key={m.id} className="bg-white rounded-xl p-3 flex items-center gap-3 border border-gray-100" data-testid={`chatbot-team-${m.id}`}>
                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs flex-shrink-0">
                                        {m.name?.split(" ").map(w => w[0]).join("").slice(0, 2)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                                        <p className="text-[11px] text-gray-500">{m.title || m.role_label}</p>
                                    </div>
                                    {m.phone && (
                                        <a href={`tel:${m.phone}`} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium bg-green-50 rounded-full px-2.5 py-1">
                                            <Phone size={11} /> Volat
                                        </a>
                                    )}
                                </div>
                            ))}
                            <button onClick={() => setShowTeam(false)} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-2">Zpět na chat</button>
                        </div>
                    ) : (
                        /* Chat */
                        <>
                            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50" data-testid="chatbot-messages">
                                {messages.map((msg, i) => (
                                    <div key={i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                                        {msg.role === "agent" && (
                                            <div className="w-7 h-7 rounded-full bg-green-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5">
                                                {initials}
                                            </div>
                                        )}
                                        <div className={`max-w-[80%] ${msg.role === "user" ? "ml-auto" : ""}`}>
                                            <div className={`rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${msg.role === "user"
                                                ? "bg-green-600 text-white rounded-br-md"
                                                : "bg-white text-gray-800 border border-gray-100 rounded-bl-md shadow-sm"
                                            }`} data-testid={`chatbot-msg-${i}`}>
                                                {msg.text}
                                            </div>
                                            <p className={`text-[9px] mt-0.5 px-1 ${msg.role === "user" ? "text-right text-gray-400" : "text-gray-400"}`}>
                                                {formatTime(msg.time)}
                                                {msg.role === "user" && <span className="ml-1 text-green-500">Přečteno</span>}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                {typing && (
                                    <div className="flex gap-2">
                                        <div className="w-7 h-7 rounded-full bg-green-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                                            {initials}
                                        </div>
                                        <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm">
                                            <p className="text-[11px] text-gray-500 mb-1">{agent?.name?.split(" ")[0] || "Agent"} píše...</p>
                                            <div className="flex gap-1">
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Quick actions */}
                            {messages.length <= 1 && (
                                <div className="px-4 pb-2 flex flex-wrap gap-1.5 bg-gray-50" data-testid="chatbot-quick-actions">
                                    {["Mám zájem o FVE", "Ceník", "Dotace 2026", "Chci nabídku"].map(q => (
                                        <button key={q} onClick={() => setInput(q)}
                                            className="text-[11px] bg-white text-gray-700 border border-gray-200 rounded-full px-3 py-1 hover:border-green-300 hover:text-green-700 transition-colors">
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Input */}
                            <div className="px-3 py-2 border-t bg-white flex-shrink-0">
                                <form onSubmit={e => { e.preventDefault(); handleSend(); }} className="flex items-center gap-2">
                                    <input value={input} onChange={e => setInput(e.target.value)}
                                        placeholder="Napište zprávu..."
                                        className="flex-1 h-9 px-3 text-[13px] bg-gray-50 rounded-full border border-gray-200 focus:bg-white focus:outline-none focus:border-green-400 transition-colors"
                                        data-testid="chatbot-input" disabled={loading} />
                                    <button type="submit" disabled={!input.trim() || loading}
                                        className="h-9 w-9 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 transition-colors disabled:opacity-40 flex-shrink-0"
                                        data-testid="chatbot-send-btn">
                                        <Send size={14} />
                                    </button>
                                </form>
                                <p className="text-[9px] text-center text-gray-300 mt-1">Solar-Green.cz</p>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
}
